import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { api, request } from '../api/client';
import type { components } from '../api/schema.gen';
import { AlertDialog } from '../components/AlertDialog';
import { EmptyState } from '../components/EmptyState';
import { PageHeading } from '../components/PageHeading';
import { RequestTopUpModal } from '../components/RequestTopUpModal';
import { SelectMenu } from '../components/SelectMenu';
import { gatewayChatCompletionsUrl, gatewayHealthUrl, gatewayUrl, playgroundEnabled } from '../lib/gatewayUrl';
import { useLanguage } from '../lib/i18n/LanguageContext';
import { fadeUp } from '../lib/motionPresets';
import { useSession } from '../lib/SessionContext';

type Model = components['schemas']['Model'];

// After a billed turn, re-read GET /balance so the topbar chip moves without
// a page reload. The server prefers the Redis hot store (already decremented
// when this response returned); a couple of short retries cover a missing
// key falling back to the ledger. We never subtract locally — the contract
// forbids deriving the displayed figure from a usage stream.
const BALANCE_POLL_MS = 800;
const BALANCE_POLL_FOR_MS = 4_000;

type Role = 'user' | 'assistant';

interface Turn {
  id: string;
  role: Role;
  content: string;
  error?: boolean;
  pending?: boolean;
  receipt?: string[];
  raw?: string;
  // LLMR-70: the free-model weekly quota is exhausted and auto-fallback is
  // off, so the gateway blocked this call outright — not a rate limit
  // (self-resolving in under a minute) and not an oversized-prompt rejection
  // (nothing an admin can do about that), so only these two codes offer the
  // nudge button.
  nudgeEligible?: boolean;
  /** Gateway refused the call because the wallet cannot cover it (HTTP 402). */
  creditShortage?: boolean;
}

/** Only the fields this page actually reads from the gateway's OpenAI-shaped
 * response — not the full contract, which belongs to the gateway's own API,
 * not this app's. */
interface ChatCompletionResponse {
  model?: string;
  choices?: { message?: { content?: string } }[];
  usage?: { prompt_tokens?: number; completion_tokens?: number };
  error?: { message?: string };
}

/** Active free-model promo window — same signal as Models.tsx (`free_until`). */
function isFreeModel(m: Pick<Model, 'free_until'>): boolean {
  return m.free_until != null && m.free_until !== '';
}

export function Playground() {
  const { dict } = useLanguage();
  const url = playgroundEnabled() ? gatewayUrl() : null;

  if (!url) {
    return (
      <div className="page">
        <PageHeading subtitle={dict.playground.subtitle}>{dict.common.playground}</PageHeading>
        <EmptyState title={dict.playground.unavailableTitle}>
          <p className="note">{dict.playground.unavailableBody}</p>
        </EmptyState>
      </div>
    );
  }

  return <PlaygroundReady defaultUrl={url} />;
}

function PlaygroundReady({ defaultUrl }: { defaultUrl: string }) {
  const { dict } = useLanguage();
  const { refreshBalance, depleted, everFunded, canManageBilling, isB2BOrg } = useSession();
  const balancePollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const balancePollUntilRef = useRef(0);
  // Autoselect the first free model at most once per page visit when the
  // wallet is empty — do not fight a later manual pick.
  const didAutoselectFreeRef = useRef(false);

  // Not editable (see the "lock the Gateway URL" request): this page calls the
  // gateway directly from the browser with a customer-pasted key (gatewayUrl.ts),
  // and that gateway is only ever the one baked in at build time via
  // PUBLIC_GATEWAY_URL — there is no second gateway a dev user should be
  // pointing this at. `gwUrl` was live-editable state before; kept as a const
  // instead of state so there is nothing to reset when `defaultUrl` can't change.
  const gwUrl = defaultUrl;
  // Not persisted (localStorage or otherwise) — like the conversation below,
  // a pasted key and the chosen model reset every time this page is entered.
  const [apiKey, setApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  // The key minted for the logged-in member (LLMR-50). Held in memory only,
  // never written to localStorage the way a pasted key is — it is a live
  // credential the member did not choose to store. A pasted key, when present,
  // takes precedence (see effectiveKey in send()), so a dev can still test some
  // other org's key by typing it in.
  const [sessionKey, setSessionKey] = useState('');
  const [sessionErr, setSessionErr] = useState(false);
  const [model, setModel] = useState<string>('');
  // The real catalogue, not a hardcoded shortlist — a model added to the
  // catalogue shows up here on the next load, same as it does on the Models
  // page. `null` means "still loading"; `[]` means the fetch failed or the
  // org has no models yet. Free models (active `free_until`) are sorted first.
  const [modelOptions, setModelOptions] = useState<Model[] | null>(null);
  const [status, setStatus] = useState<'idle' | 'testing' | 'ok' | 'err'>('idle');
  const [turns, setTurns] = useState<Turn[]>([]);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  // Themed stand-in for the old `window.alert` checks before send().
  const [sendAlert, setSendAlert] = useState<string | null>(null);
  const [requestTopUpOpen, setRequestTopUpOpen] = useState(false);
  const [requestTopUpResult, setRequestTopUpResult] = useState<'idle' | 'sent' | 'cooldown' | 'error'>('idle');
  const threadEndRef = useRef<HTMLDivElement>(null);
  const historyRef = useRef<{ role: Role; content: string }[]>([]);

  const freeModels = useMemo(() => (modelOptions ?? []).filter(isFreeModel), [modelOptions]);
  const hasFreeModels = freeModels.length > 0;
  // GET /balance/status is open to every role — use it as the zero-credit
  // signal so plain members (who never see the amount) get the same guidance.
  const outOfCredit = depleted === true;
  const spentThrough = everFunded === true;

  // page_size 200 (the API's max) rather than paging: this dropdown wants
  // the whole catalogue in one shot, not a page of it like the Models table.
  //
  // LLMR-136: filtered server-side to mode: 'chat'. This is specifically the
  // chat completions form — an embedding model (cohere/embed-4,
  // text-embedding-3-*, ...) picked here reaches the gateway malformed and
  // the customer sees a raw litellm.BadRequestError from Bedrock. The Models
  // page (Models.tsx) has its own mode filter and defaults to unfiltered;
  // only this picker always narrows to chat.
  useEffect(() => {
    let cancelled = false;
    api
      .models({ page_size: 200, sort: 'model_id', order: 'asc', mode: 'chat' })
      .then((res) => {
        if (cancelled) return;
        const sorted = [...res.data].sort((a, b) => {
          const af = isFreeModel(a) ? 0 : 1;
          const bf = isFreeModel(b) ? 0 : 1;
          if (af !== bf) return af - bf;
          return a.model_id.localeCompare(b.model_id);
        });
        setModelOptions(sorted);
      })
      .catch(() => {
        if (!cancelled) setModelOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // When the wallet is empty and free models exist, pre-select the first free
  // one once catalogue + depleted status are both known.
  useEffect(() => {
    if (didAutoselectFreeRef.current) return;
    if (!outOfCredit || modelOptions === null || freeModels.length === 0) return;
    if (model.trim()) return;
    const firstFree = freeModels[0];
    if (!firstFree) return;
    didAutoselectFreeRef.current = true;
    setModel(firstFree.model_id);
  }, [outOfCredit, modelOptions, freeModels, model]);

  // Mint a key for the signed-in member so the Playground works without a
  // pasted one. The gateway only authenticates against its key cache, not the
  // member session, so member-api turns the session into a real org-scoped key
  // and hands back its secret (LLMR-50). On failure the page stays usable — the
  // member can still paste a key by hand.
  //
  // At most one playground key is ever live per org (MintPlaygroundKey revokes
  // the previous one on every call), so a second tab, a page reload elsewhere,
  // or another team member opening the Playground silently invalidates
  // whatever key THIS page is holding — the next send() would 401 with no
  // indication why. mintSession is shared between this mount effect and
  // send()'s retry-on-401 path below, which re-runs exactly this to recover.
  async function mintSession(): Promise<string | null> {
    try {
      const res = await request<{ secret: string }>('/playground/session', { method: 'POST' });
      setSessionKey(res.secret);
      setSessionErr(false);
      return res.secret;
    } catch {
      setSessionErr(true);
      return null;
    }
  }

  useEffect(() => {
    void mintSession();
  }, []);

  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ block: 'end' });
  }, [turns]);

  // Pull a fresh GET /balance after a billed turn. A second send while the
  // previous window is still open just extends the deadline.
  function watchBalance() {
    void refreshBalance();
    balancePollUntilRef.current = Date.now() + BALANCE_POLL_FOR_MS;
    if (balancePollRef.current) return;
    balancePollRef.current = setInterval(() => {
      if (Date.now() >= balancePollUntilRef.current) {
        if (balancePollRef.current) {
          clearInterval(balancePollRef.current);
          balancePollRef.current = null;
        }
        return;
      }
      void refreshBalance();
    }, BALANCE_POLL_MS);
  }

  useEffect(() => {
    return () => {
      if (balancePollRef.current) clearInterval(balancePollRef.current);
    };
  }, []);

  async function testConnection() {
    setStatus('testing');
    const healthUrl = gatewayHealthUrl();
    if (!healthUrl) {
      setStatus('err');
      return;
    }
    try {
      const res = await fetch(healthUrl);
      setStatus(res.ok ? 'ok' : 'err');
    } catch {
      setStatus('err');
    }
  }

  // A pasted key wins; otherwise fall back to the session key minted for the
  // logged-in member. Only truly keyless (paste empty AND session mint failed)
  // is a hard stop.
  const usingSessionKey = !apiKey.trim();
  const effectiveKey = apiKey.trim() || sessionKey;

  function postChat(key: string) {
    const chatUrl = gatewayChatCompletionsUrl();
    if (!chatUrl) throw new Error('gateway URL not configured');
    return fetch(chatUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${key}` },
      body: JSON.stringify({ model: model.trim(), max_tokens: 600, messages: historyRef.current }),
    });
  }

  async function send() {
    const content = message.trim();
    if (!content) return;
    if (!effectiveKey) {
      setSendAlert(dict.playground.needKeyAlert);
      return;
    }
    if (!model.trim()) {
      setSendAlert(dict.playground.needModelAlert);
      return;
    }

    const userTurn: Turn = { id: crypto.randomUUID(), role: 'user', content };
    const pendingId = crypto.randomUUID();
    historyRef.current = [...historyRef.current, { role: 'user' as Role, content }].slice(-12);
    setTurns((prev) => [...prev, userTurn, { id: pendingId, role: 'assistant', content: '', pending: true }]);
    setMessage('');
    setSending(true);

    const started = performance.now();
    try {
      let res = await postChat(effectiveKey);
      // A 401 while on the session key almost always means another tab, a
      // reload, or a teammate opened the Playground and re-minted — which
      // revokes this page's copy (see mintSession's doc comment) — rather
      // than a real credential problem, so it is worth one silent recovery
      // attempt. A pasted key is a member's deliberate choice; if THAT 401s,
      // substituting the session key instead would answer a question they
      // did not ask, so this never touches the pasted-key path.
      if (res.status === 401 && usingSessionKey) {
        const fresh = await mintSession();
        if (fresh) res = await postChat(fresh);
      }
      const elapsedMs = Math.round(performance.now() - started);
      const bodyText = await res.text();
      let data: ChatCompletionResponse | null = null;
      try {
        data = JSON.parse(bodyText) as ChatCompletionResponse;
      } catch {
        // Non-JSON body (e.g. an HTML error page from a misconfigured proxy)
        // falls through to the res.ok check below, which renders bodyText.
      }

      if (!res.ok) {
        const creditShortage = res.status === 402;
        const rawMsg = data?.error?.message ?? `HTTP ${res.status}`;
        const msg = creditShortage
          ? hasFreeModels
            ? `${dict.playground.creditErrorBody} ${dict.playground.creditErrorFreeHint}`
            : dict.playground.creditErrorBody
          : rawMsg;
        const nudgeEligible = !creditShortage && (rawMsg.startsWith('QUOTA_EXCEEDED:') || rawMsg.startsWith('REQUEST_LIMIT_EXCEEDED:'));
        setTurns((prev) =>
          prev.map((t) =>
            t.id === pendingId
              ? {
                  ...t,
                  pending: false,
                  error: true,
                  content: msg,
                  receipt: [`HTTP ${res.status}`, `${elapsedMs} ms`],
                  nudgeEligible,
                  creditShortage,
                }
              : t,
          ),
        );
        // 402 is the gateway refusing a call because the wallet is empty —
        // the chip should show the depleted figure, not the cached one from
        // session start. Other HTTP errors are not billed.
        if (creditShortage) watchBalance();
        // Deliberately NOT setStatus('err') here: an HTTP error response (bad model,
        // bad key, rate limit, ...) means the gateway IS reachable — it answered. Only
        // testConnection()'s own network failure should ever claim "Unreachable"; doing
        // it here too previously showed "Unreachable" on e.g. a plain 400 model-name
        // error, which is misleading about what actually failed.
        return;
      }

      const replyModel = data?.model ?? '?';
      const replyText = data?.choices?.[0]?.message?.content ?? '';
      // An empty completion (no tool_calls either) is valid OpenAI-shape but some
      // backends validate assistant history strictly — Bedrock Mantle 400s on
      // replay with "Invalid assistant message: content='' tool_calls=None" the
      // moment a later turn (any model, since history is shared across model
      // switches) sends it back. Same placeholder already used for the turn's
      // display text below, so what got shown is what gets replayed.
      historyRef.current = [
        ...historyRef.current,
        { role: 'assistant' as Role, content: replyText || '(empty response)' },
      ].slice(-12);

      const receipt = [replyModel];
      if (data?.usage?.prompt_tokens != null) receipt.push(`${data.usage.prompt_tokens} in`);
      if (data?.usage?.completion_tokens != null) receipt.push(`${data.usage.completion_tokens} out`);
      receipt.push(`${elapsedMs} ms`);

      setTurns((prev) =>
        prev.map((t) =>
          t.id === pendingId ? { ...t, pending: false, content: replyText || '(empty response)', receipt, raw: JSON.stringify(data, null, 2) } : t,
        ),
      );
      setStatus('ok');
      watchBalance();
    } catch {
      setTurns((prev) =>
        prev.map((t) => (t.id === pendingId ? { ...t, pending: false, error: true, content: dict.playground.networkErrorBody(gwUrl) } : t)),
      );
      setStatus('err');
    } finally {
      setSending(false);
    }
  }

  function clear() {
    setTurns([]);
    historyRef.current = [];
  }

  function renderTopUpCta(className = 'btn-link') {
    if (canManageBilling) {
      return (
        <Link to="/topup" className={className}>
          {dict.playground.goTopUpCta}
        </Link>
      );
    }
    if (requestTopUpResult === 'sent') {
      return <p className="note">{dict.dashboard.requestTopUpSent}</p>;
    }
    if (requestTopUpResult === 'cooldown') {
      return <p className="note">{dict.dashboard.requestTopUpCooldown}</p>;
    }
    return (
      <>
        <button type="button" className={className} onClick={() => setRequestTopUpOpen(true)}>
          {dict.dashboard.requestTopUpCta}
        </button>
        {requestTopUpResult === 'error' && <p className="note warn-text">{dict.dashboard.requestTopUpError}</p>}
      </>
    );
  }

  function emptyStateBody() {
    if (!outOfCredit) {
      return <p className="note">{dict.playground.emptyBodyReady}</p>;
    }
    if (hasFreeModels) {
      return <p className="note">{dict.playground.emptyBodyWithFree}</p>;
    }
    if (canManageBilling) {
      const parts = dict.playground.emptyBodyNeedCredit.split('{topUpLink}');
      return (
        <p className="note">
          {parts[0]}
          <Link to="/topup">{dict.playground.goTopUpCta}</Link>
          {parts[1]}
        </p>
      );
    }
    return (
      <>
        <p className="note">{dict.playground.emptyBodyNeedCreditMember(isB2BOrg)}</p>
        {renderTopUpCta()}
      </>
    );
  }

  function costNoteBody() {
    const parts = dict.playground.costNote.split('{usageLink}');
    return (
      <>
        {parts[0]}
        <Link to="/usage">{dict.playground.costNoteUsageLink}</Link>
        {parts[1]}
      </>
    );
  }

  return (
    <div className="page playground-page">
      <PageHeading subtitle={dict.playground.subtitle}>{dict.common.playground}</PageHeading>

      {outOfCredit && (
        <motion.aside variants={fadeUp} initial="hidden" animate="show" className="playground-credit-banner" role="status">
          <p className="playground-credit-banner-body">
            {hasFreeModels
              ? spentThrough
                ? dict.playground.creditBannerWithFreeSpent
                : dict.playground.creditBannerWithFree
              : spentThrough
                ? dict.playground.creditBannerNoFreeSpent
                : dict.playground.creditBannerNoFree}
          </p>
          <div className="playground-credit-banner-actions">{renderTopUpCta()}</div>
        </motion.aside>
      )}

      <motion.section variants={fadeUp} initial="hidden" animate="show" className="panel">
        <div className="panel-heading-row">
          <h2>{dict.playground.configHeading}</h2>
          <div className="playground-test-field">
            <span className={`status-pill status-pill--${status}`}>
              {status === 'testing' && dict.playground.testing}
              {status === 'ok' && dict.playground.statusConnected}
              {status === 'err' && dict.playground.statusUnreachable}
              {status === 'idle' && dict.playground.statusNotTested}
            </span>
            <button type="button" className="ghost" onClick={testConnection} disabled={status === 'testing'}>
              {dict.playground.testConnection}
            </button>
          </div>
        </div>
        <div className="playground-conn-grid">
          <div className="inline-form-field">
            <label htmlFor="pg-url">{dict.playground.gatewayUrlLabel}</label>
            <input id="pg-url" className="mono-field locked-field" value={gwUrl} readOnly aria-readonly="true" spellCheck={false} />
          </div>
          <div className="inline-form-field">
            <label htmlFor="pg-key" title={dict.playground.apiKeyOptionalHint}>
              {dict.playground.apiKeyLabel}
            </label>
            <div className="key-input-row">
              <input
                id="pg-key"
                className="mono-field"
                type={showKey ? 'text' : 'password'}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={dict.playground.apiKeyPlaceholder}
                autoComplete="off"
                spellCheck={false}
              />
              <button type="button" className="ghost key-show-toggle" onClick={() => setShowKey((v) => !v)}>
                {showKey ? dict.playground.hideKey : dict.playground.showKey}
              </button>
            </div>
          </div>
          <div className="inline-form-field">
            <label htmlFor="pg-model">{dict.playground.modelLabel}</label>
            <SelectMenu
              id="pg-model"
              aria-label={dict.playground.modelLabel}
              value={model}
              disabled={modelOptions === null}
              options={
                modelOptions === null
                  ? [{ value: '', label: dict.common.loading }]
                  : [
                      { value: '', label: dict.playground.modelPlaceholder },
                      ...modelOptions.map((m) => ({
                        value: m.model_id,
                        label: m.model_id,
                        badge: isFreeModel(m) ? dict.playground.freeBadge : undefined,
                      })),
                    ]
              }
              onChange={(v) => {
                setModel(v);
              }}
              searchable
              searchHint={dict.playground.modelSearchHint}
              noResultsLabel={dict.playground.modelNoResults}
              clearLabel={dict.common.clear}
            />
          </div>
        </div>
        {/* Full-width under the row so a long session error does not stretch one
            column and yank the key input up relative to URL / model (flex-end). */}
        {sessionErr && (
          <p className="note playground-key-hint playground-key-hint--err" role="status">
            {dict.playground.sessionError}
          </p>
        )}
      </motion.section>

      <motion.section variants={fadeUp} initial="hidden" animate="show" className="panel playground-thread-panel">
        <h2>{dict.playground.chatHeading}</h2>
        {turns.length === 0 ? (
          <EmptyState title={dict.playground.emptyTitle}>
            {emptyStateBody()}
            <p className="note playground-cost-note">{costNoteBody()}</p>
          </EmptyState>
        ) : (
          <div className="playground-thread">
            {turns.map((t) => (
              <PlaygroundTurn
                key={t.id}
                turn={t}
                viewRawLabel={dict.playground.viewRawResponse}
                hideRawLabel={dict.playground.hideRawResponse}
              />
            ))}
            <div ref={threadEndRef} />
          </div>
        )}

        <div className="playground-composer">
          <textarea
            className="playground-input"
            rows={2}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void send();
              }
            }}
            placeholder={dict.playground.messagePlaceholder}
          />
          <button type="button" className="playground-send" onClick={() => void send()} disabled={sending} aria-label={dict.playground.send}>
            ➤
          </button>
        </div>
        <div className="playground-footer-row">
          <span className="note">{dict.playground.messagesSentCount(turns.filter((t) => t.role === 'user').length)}</span>
          <button type="button" className="ghost" onClick={clear} disabled={turns.length === 0}>
            {dict.playground.clearConversation}
          </button>
        </div>
      </motion.section>

      <AnimatePresence>
        {sendAlert && (
          <AlertDialog
            title={dict.playground.needAlertTitle}
            body={<p>{sendAlert}</p>}
            onClose={() => setSendAlert(null)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {requestTopUpOpen && (
          <RequestTopUpModal
            reason="balance"
            title={dict.dashboard.requestTopUpCta}
            cta={dict.dashboard.requestTopUpSendTo}
            onClose={() => setRequestTopUpOpen(false)}
            onResult={setRequestTopUpResult}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function PlaygroundTurn({
  turn,
  viewRawLabel,
  hideRawLabel,
}: {
  turn: Turn;
  viewRawLabel: string;
  hideRawLabel: string;
}) {
  const [rawOpen, setRawOpen] = useState(false);
  const { dict } = useLanguage();
  const { canManageBilling, isB2BOrg } = useSession();
  const [nudgeModalOpen, setNudgeModalOpen] = useState(false);
  const [nudgeResult, setNudgeResult] = useState<'idle' | 'sent' | 'cooldown' | 'error'>('idle');
  const [creditRequestOpen, setCreditRequestOpen] = useState(false);
  const [creditRequestResult, setCreditRequestResult] = useState<'idle' | 'sent' | 'cooldown' | 'error'>('idle');

  return (
    <div className={`playground-turn playground-turn--${turn.role}`}>
      <div className={`playground-bubble${turn.error ? ' playground-bubble--err' : ''}`}>
        {turn.pending ? (
          <span className="playground-typing" aria-hidden="true">
            <span />
            <span />
            <span />
          </span>
        ) : (
          turn.content
        )}
      </div>
      {turn.role === 'assistant' && !turn.pending && turn.receipt && (
        <div className="playground-receipt">
          {turn.receipt.map((tag, i) => (
            <span key={i} className={`playground-tag${i === 0 ? ' playground-tag--model' : ''}`}>
              {tag}
            </span>
          ))}
        </div>
      )}
      {turn.creditShortage && (
        <div className="playground-nudge">
          {canManageBilling ? (
            <Link to="/topup" className="btn-link">
              {dict.playground.goTopUpCta}
            </Link>
          ) : creditRequestResult === 'sent' ? (
            <p className="note">{dict.dashboard.requestTopUpSent}</p>
          ) : creditRequestResult === 'cooldown' ? (
            <p className="note">{dict.dashboard.requestTopUpCooldown}</p>
          ) : (
            <button type="button" className="btn-link" onClick={() => setCreditRequestOpen(true)}>
              {dict.dashboard.requestTopUpCta}
            </button>
          )}
          {creditRequestResult === 'error' && <p className="note warn-text">{dict.dashboard.requestTopUpError}</p>}
        </div>
      )}
      {turn.raw && (
        <>
          <button type="button" className="playground-raw-toggle" onClick={() => setRawOpen((v) => !v)}>
            {rawOpen ? hideRawLabel : viewRawLabel}
          </button>
          <AnimatePresence>
            {rawOpen && (
              <motion.pre
                className="playground-raw"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
              >
                {turn.raw}
              </motion.pre>
            )}
          </AnimatePresence>
        </>
      )}
      {/* LLMR-70: admin/owner can just fix this themselves (turn on
          auto-fallback, or wait for Monday) — the server 403s them on this
          endpoint anyway, so it is not offered here either. */}
      {turn.nudgeEligible && !canManageBilling && (
        <div className="playground-nudge">
          {nudgeResult === 'sent' ? (
            <p className="note">{dict.dashboard.requestTopUpSent}</p>
          ) : nudgeResult === 'cooldown' ? (
            <p className="note">{dict.dashboard.requestTopUpCooldown}</p>
          ) : (
            <button type="button" className="btn-link" onClick={() => setNudgeModalOpen(true)}>
              {dict.playground.nudgeAdminCta(isB2BOrg)}
            </button>
          )}
          {nudgeResult === 'error' && <p className="note warn-text">{dict.dashboard.requestTopUpError}</p>}
        </div>
      )}
      <AnimatePresence>
        {nudgeModalOpen && (
          <RequestTopUpModal
            reason="quota"
            title={dict.playground.nudgeAdminTitle(isB2BOrg)}
            cta={dict.dashboard.requestTopUpSendTo}
            onClose={() => setNudgeModalOpen(false)}
            onResult={setNudgeResult}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {creditRequestOpen && (
          <RequestTopUpModal
            reason="balance"
            title={dict.dashboard.requestTopUpCta}
            cta={dict.dashboard.requestTopUpSendTo}
            onClose={() => setCreditRequestOpen(false)}
            onResult={setCreditRequestResult}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
